import type { ReactNode } from 'react'
import type {
  AlertDialogFooterProps,
  AlertDialogHeaderProps,
  AlertDialogProps,
} from './AlertDialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './AlertDialog'
import { Button } from './Button'
import type { ButtonProps } from './Button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog'
import type { DialogProps } from './Dialog'
import type { SetOptional } from 'type-fest'
import { createNonNullableContext, cn } from '@joyful/utils'
import { useControllableState } from '@radix-ui/react-use-controllable-state'

export interface ConfirmationDialogProps extends DialogProps {
  children: ReactNode

  /**
   * @default 'Confirm'
   */
  readonly confirmButtonText?: string
  readonly confirmButtonVariant?: ButtonProps['variant']
  readonly confirmationDescription?: ReactNode
  /**
   * @default 'Are you sure?'
   */
  readonly confirmationTitle?: ReactNode
  readonly performAction: () => void
}

export function ConfirmationDialog({
  children,
  confirmButtonText = 'Confirm',
  confirmButtonVariant = 'brand-subtle',
  confirmationDescription,
  confirmationTitle = 'Are you sure?',
  performAction,
  ...dialogProps
}: ConfirmationDialogProps) {
  return (
    <Dialog {...dialogProps}>
      {children}

      <DialogContent>
        <DialogHeader>
          {typeof confirmationTitle === 'string' ? (
            <DialogTitle>{confirmationTitle}</DialogTitle>
          ) : (
            confirmationTitle
          )}
        </DialogHeader>

        {confirmationDescription !== undefined && (
          <DialogBody>
            {typeof confirmationDescription === 'string' ? (
              <DialogDescription>{confirmationDescription}</DialogDescription>
            ) : (
              confirmationDescription
            )}
          </DialogBody>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>

          <DialogClose asChild>
            <Button
              variant={confirmButtonVariant}
              onClick={() => {
                performAction()
              }}
            >
              {confirmButtonText}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const ConfirmationAlertDialogContent = AlertDialogContent

export function ConfirmationAlertDialogHeader({
  children,
  ...props
}: AlertDialogHeaderProps) {
  return (
    <AlertDialogHeader {...props}>
      {typeof children === 'string' ? (
        <AlertDialogTitle>{children}</AlertDialogTitle>
      ) : (
        children
      )}
    </AlertDialogHeader>
  )
}

export const ConfirmationAlertDialogDescription = AlertDialogDescription

interface ConfirmationAlertDialogContextValue {
  isConfirmationDialogOpen: boolean
  setConfirmationDialogOpen: (isOpen: boolean) => void
}

const ConfirmationAlertDialogContext =
  createNonNullableContext<ConfirmationAlertDialogContextValue>(
    'ConfirmationAlertDialogContext',
  )

export interface ConfirmationAlertDialogProps extends AlertDialogProps {
  /**
   * when locked, the dialog cannot be opened or closed.
   * helpful when you want to prevent closing during a pending mutation
   *
   * @default false
   */
  isLocked?: boolean
}

export function ConfirmationAlertDialog({
  children,
  isLocked = false,
  onOpenChange,
  ...props
}: ConfirmationAlertDialogProps) {
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] =
    useControllableState({
      defaultProp: props.defaultOpen ?? false,
      onChange: onOpenChange,
      prop: props.open,
    })

  return (
    <ConfirmationAlertDialogContext.Context
      value={{
        isConfirmationDialogOpen,
        setConfirmationDialogOpen,
      }}
    >
      <AlertDialog
        open={isConfirmationDialogOpen}
        onOpenChange={(nextOpen) => {
          if (isLocked === false) {
            setConfirmationDialogOpen(nextOpen)
          }
        }}
        {...props}
      >
        {children}
      </AlertDialog>
    </ConfirmationAlertDialogContext.Context>
  )
}

export const ConfirmationAlertDialogTrigger: typeof AlertDialogTrigger =
  AlertDialogTrigger

export interface ConfirmationAlertDialogFooterProps extends AlertDialogFooterProps {
  children: ReactNode
  footerAdornment?: ReactNode
}

export function ConfirmationAlertDialogFooter({
  children,
  footerAdornment,
  ...props
}: ConfirmationAlertDialogFooterProps) {
  return (
    <AlertDialogFooter {...props}>
      <div
        className={cn('flex-[1_0_0]', !footerAdornment && 'hidden md:block')}
      >
        {footerAdornment}
      </div>
      {children}
    </AlertDialogFooter>
  )
}

export function ConfirmationAlertDialogCancelButton({
  children = 'Cancel',
  ...props
}: SetOptional<ButtonProps, 'children'>) {
  return <AlertDialogCancel {...props}>{children}</AlertDialogCancel>
}

export const ConfirmationAlertDialogAction = AlertDialogAction

export function ConfirmationAlertDialogActionButton({
  children = 'Confirm',
  variant = 'destructive',
  ...props
}: SetOptional<ButtonProps, 'children'>) {
  return (
    <AlertDialogAction variant={variant} {...props}>
      {children}
    </AlertDialogAction>
  )
}
